import React, { useState, useMemo } from 'react';
import Page from '../components/Page';
import { useCurrentRegister, useRegisterByDate, reassignInvoiceDate } from '../controllers/financial.controller';
import { useStoreSettings, usePrintSettings } from '../controllers/settings.controller';
import { getInvoiceOrders } from '../controllers/invoices.controller';
import { printReceipt } from '../helpers/PrinterHelper';
import { setDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import { 
  IconEye, 
  IconPrinter, 
  IconCalendar, 
  IconRefresh, 
  IconSearch, 
  IconCheck, 
  IconX,
  IconCoins,
  IconArrowUpRight,
  IconArrowDownRight,
  IconUsers,
  IconReceipt,
  IconFileText,
  IconArrowsExchange
} from '@tabler/icons-react';
import FinancialReportsPrintModal from '../components/FinancialReportsPrintModal';
import { toast } from 'react-hot-toast';

export default function FinancialRegisterPage() {
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'closed' | 'reports'
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });

  // Drilldown modal state
  const [drillModal, setDrillModal] = useState({ isOpen: false, title: '', items: [] });
  const [drillSearch, setDrillSearch] = useState('');
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [selectedShiftForReport, setSelectedShiftForReport] = useState(null);
  const [printModalConfig, setPrintModalConfig] = useState({ isOpen: false, reportType: 'register', shift: null });

  // Reassign date modal state
  const [reassignModal, setReassignModal] = useState({
    isOpen: false,
    invoices: [],
    targetDate: '',
    targetTime: '23:55'
  });
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [isReassigning, setIsReassigning] = useState(false);

  // Formatting helpers
  const fmt = (v) => {
    const num = Number(String(v || 0).replace(/,/g, ""));
    return isNaN(num) ? "0.00" : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    try {
      const d = new Date(dt);
      return isNaN(d.getTime()) ? String(dt) : d.toLocaleString("en-GB", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true
      });
    } catch {
      return String(dt);
    }
  };

  const parseDenoms = (d) => {
    if (!d) return [];
    let raw = d;
    if (typeof raw === "string") {
      try { raw = JSON.parse(raw); } catch (e) { return []; }
    }
    if (!raw) return [];
    if (Array.isArray(raw)) {
      if (raw.length > 0 && (raw[0].breakdown || raw[0].items)) return raw;
      if (raw.length > 0 && raw[0].denomination !== undefined) {
        const subtotal = raw.reduce((sum, it) => sum + (Number(it.denomination) * Number(it.count || 0)), 0);
        return [{
          currency_code: currency,
          is_base: true,
          currency_total: subtotal,
          breakdown: raw.map(it => ({
            denomination: Number(it.denomination),
            count: Number(it.count || 0),
            total: Number(it.total || (Number(it.denomination) * Number(it.count || 0)))
          }))
        }];
      }
      return raw;
    }
    if (typeof raw === "object") {
      const keys = Object.keys(raw);
      if (keys.length === 0) return [];
      const firstVal = raw[keys[0]];
      const isNested = typeof firstVal === "object" && firstVal !== null && !Array.isArray(firstVal);
      if (isNested) {
        return keys.map(currKey => {
          const bObj = raw[currKey] || {};
          const breakdown = Object.entries(bObj)
            .map(([denom, count]) => ({
              denomination: Number(denom),
              count: Number(count || 0),
              total: Number(denom) * Number(count || 0)
            }))
            .filter(item => item.count > 0 || item.denomination > 0)
            .sort((a, b) => b.denomination - a.denomination);
          return {
            currency_code: currKey,
            currency_total: breakdown.reduce((acc, it) => acc + it.total, 0),
            breakdown
          };
        });
      } else {
        const breakdown = Object.entries(raw)
          .map(([denom, count]) => ({
            denomination: Number(denom),
            count: Number(count || 0),
            total: Number(denom) * Number(count || 0)
          }))
          .filter(item => item.count > 0 || item.denomination > 0)
          .sort((a, b) => b.denomination - a.denomination);
        return [{
          currency_code: currency,
          currency_total: breakdown.reduce((acc, it) => acc + it.total, 0),
          breakdown
        }];
      }
    }
    return [];
  };

  // SWR queries
  const { data: currentData, isLoading: isLoadingCurrent, mutate: mutateCurrent } = useCurrentRegister();
  const { data: byDateData, isLoading: isLoadingDate, mutate: mutateDate } = useRegisterByDate(activeTab !== 'current' ? selectedDate : null);
  const { data: storeSettings } = useStoreSettings();
  const { data: printSettings } = usePrintSettings();

  const data = activeTab === 'current' ? currentData : byDateData;
  const isLoading = activeTab === 'current' ? isLoadingCurrent : isLoadingDate;
  const mutate = activeTab === 'current' ? mutateCurrent : mutateDate;

  const currency = storeSettings?.currency || "NPR";
  const register = data?.register || {};
  const shifts = data?.shifts || [];
  const payments = data?.payments || [];
  const expenses = data?.expenses || [];
  const invoices = data?.invoices || {};
  const transactions = data?.transactions || [];

  const businessDate = register.business_date || selectedDate;

  // Format date display
  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  // Sales totals
  const grossSales = Number(invoices.total_gross || 0);
  const discount = Number(invoices.total_discount || 0);
  const tax = Number(invoices.total_tax || 0);
  const roundOff = Number(invoices.total_round_off || 0);
  const complementary = Number(invoices.total_complementary || 0);
  const netSales = grossSales - discount;
  const totalBilled = Number(invoices.total_billed || (netSales + tax + roundOff));
  const salesDue = Number(invoices.due_sales || 0);

  // Collections totals
  let totalCollected = 0;
  payments.forEach(p => totalCollected += Number(p.total || 0));

  // Cash reconciliation for Register
  const cashPayments = Number(payments.find(p => (p.payment_method || '').toLowerCase() === 'cash')?.total || 0);
  const cashExpenses = Number(expenses.find(e => (e.payment_method || '').toLowerCase() === 'cash')?.total || 0);
  const netCashMovement = cashPayments - cashExpenses;

  // Drilldown modal opener
  const openDrillDown = (title, filterFn) => {
    const items = filterFn ? transactions.filter(filterFn) : transactions;
    setDrillModal({ isOpen: true, title, items });
    setDrillSearch('');
    setSelectedInvoiceIds([]);
  };

  const openReassignModal = (itemsToReassign) => {
    if (!itemsToReassign || itemsToReassign.length === 0) {
      toast.error("Please select at least one invoice to reassign.");
      return;
    }

    const firstInv = itemsToReassign[0];
    const createdD = firstInv?.created_at ? new Date(firstInv.created_at) : new Date();
    const d = new Date(createdD);
    if (createdD.getHours() < 6) {
      d.setDate(d.getDate() - 1);
    }
    const defDate = d.toISOString().split('T')[0];

    setReassignModal({
      isOpen: true,
      invoices: itemsToReassign,
      targetDate: defDate,
      targetTime: '23:55'
    });
  };

  const handleConfirmReassign = async () => {
    if (!reassignModal.invoices || reassignModal.invoices.length === 0) return;
    if (!reassignModal.targetDate) {
      toast.error("Please select a target business date.");
      return;
    }

    try {
      setIsReassigning(true);
      const invoiceIds = reassignModal.invoices.map(it => it.id);
      const res = await reassignInvoiceDate(invoiceIds, reassignModal.targetDate, reassignModal.targetTime);
      toast.success(res?.message || `Successfully appended ${invoiceIds.length} invoice(s) to ${reassignModal.targetDate}!`);
      setReassignModal({ isOpen: false, invoices: [], targetDate: '', targetTime: '23:55' });
      setSelectedInvoiceIds([]);
      setDrillModal(prev => ({ ...prev, isOpen: false }));
      if (mutateCurrent) await mutateCurrent();
      if (mutateDate) await mutateDate();
    } catch (err) {
      console.error("Reassign date error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to reassign invoice sales date.");
    } finally {
      setIsReassigning(false);
    }
  };

  const filteredDrillItems = useMemo(() => {
    if (!drillSearch) return drillModal.items;
    const q = drillSearch.toLowerCase();
    return drillModal.items.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q))
      );
    });
  }, [drillModal.items, drillSearch]);

  // Open invoice detailed breakdown modal
  const handleViewInvoice = async (invoice) => {
    try {
      setIsLoadingInvoice(true);
      toast.loading("Loading transaction details...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders:", err);
      }
      toast.dismiss();

      setViewingInvoice({
        ...invoice,
        ...(orderData || {}),
        invoiceId: invoiceId,
        invoiceNo: invoice.invoice_no || orderData?.invoice_no || `INV-${invoiceId}`
      });
    } catch (err) {
      toast.dismiss();
      console.error("Error viewing invoice:", err);
      setViewingInvoice({
        ...invoice,
        orders: [],
        invoiceId: invoice.id,
        invoiceNo: invoice.invoice_no || `INV-${invoice.id}`
      });
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // Print thermal receipt for single transaction
  const handlePrintTransaction = async (invoice) => {
    try {
      toast.loading("Preparing receipt...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders, using invoice record:", err);
      }
      toast.dismiss();

      const ordersArr = orderData?.orders || [];
      let cartItems = [];

      for (const o of ordersArr) {
        (o.items || []).forEach(i => {
          cartItems.push({
            ...i,
            title: i.item_title || i.title || "Item",
            addons_ids: i?.addons?.length > 0 ? i.addons.map(a => a.id) : [],
            variant: i.variant_id ? { id: i.variant_id, title: i.variant_title, price: i.variant_price } : null
          });
        });
      }

      if (cartItems.length === 0) {
        cartItems.push({
          title: invoice.item_name || invoice.invoice_no || `Invoice #${invoiceId}`,
          quantity: invoice.quantity || 1,
          price: Number(invoice.sub_total || invoice.total || 0)
        });
      }

      const billFormat = printSettings?.bill_format || localStorage.getItem("bill_format") || "retail_tax_invoice";
      const pageFormat = printSettings?.page_format || localStorage.getItem("page_format") || "58";

      const receiptDetails = {
        cartItems,
        customerType: "CUSTOMER",
        customer: {
          id: invoice.customer_id || "Walk-in",
          name: invoice.customer_name || "Walk-in Customer"
        },
        tableId: null,
        currency: currency,
        storeSettings: storeSettings || {},
        printSettings: printSettings || {},
        billFormat,
        pageFormat,
        itemsTotal: Number(orderData?.subtotal !== undefined ? orderData.subtotal : (invoice.sub_total || invoice.total || 0)),
        taxTotal: Number(orderData?.taxTotal !== undefined ? orderData.taxTotal : (invoice.tax_total || 0)),
        serviceChargeTotal: Number(orderData?.serviceChargeTotal || 0),
        payableTotal: Number(orderData?.total !== undefined ? orderData.total : (invoice.total || 0)),
        storedDiscount: Number(orderData?.discount !== undefined ? orderData.discount : (invoice.discount || 0)),
        roundOff: Number(orderData?.roundOff !== undefined ? orderData.roundOff : (invoice.round_off || 0)),
        paidAmount: Number(orderData?.paidAmount !== undefined ? orderData.paidAmount : (invoice.paid_amount !== undefined ? invoice.paid_amount : invoice.total)),
        dueAmount: Number(orderData?.dueAmount !== undefined ? orderData.dueAmount : (invoice.due_amount || 0)),
        tokenNo: invoice.token_no,
        orderId: String(invoiceId),
        created_at: invoice.created_at || invoice.date || new Date(),
        date: invoice.created_at || invoice.date || new Date(),
        invoiceId: invoiceId,
        invoice_id: invoiceId,
        billNo: invoice.invoice_no || `INV-${invoiceId}`,
        invoiceNo: invoice.invoice_no || `INV-${invoiceId}`,
        paymentMethod: invoice.payment_method_name || "Cash"
      };

      setDetailsForReceiptPrint(receiptDetails);
      await printReceipt(receiptDetails);
      toast.success("Receipt sent to printer!");
    } catch (err) {
      toast.dismiss();
      console.error("Print receipt error:", err);
      toast.error("Failed to print receipt");
    }
  };

  return (
    <Page>
      <div className="container-xl">
        {/* Navigation Section 1 */}
        <div className="d-print-none mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="page-title text-primary fw-bold mb-0">REGISTER</h2>
              <div className="text-muted small">Complete Business Date Financial Control & Multi-Shift Audit</div>
            </div>
            <div className="btn-group shadow-sm">
              <button 
                className={`btn btn-sm ${activeTab === 'current' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('current')}
              >
                Current Register
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'closed' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('closed')}
              >
                Closed Registers
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'reports' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('reports')}
              >
                Register Reports
              </button>
            </div>
          </div>
        </div>

        {/* Date Selector for Closed Registers */}
        {activeTab !== 'current' && (
          <div className="card shadow-sm border-0 mb-3 d-print-none bg-white">
            <div className="card-body p-3 d-flex align-items-center gap-3">
              <span className="fw-bold text-muted small text-uppercase">Select Business Date:</span>
              <input 
                type="date" 
                className="form-control form-control-sm"
                style={{ width: '160px' }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <button className="btn btn-sm btn-dark" onClick={() => mutate()}>Load Register</button>
            </div>
          </div>
        )}

        {/* SECTION 13: REGISTER MAIN CARD */}
        <div className="card shadow-sm border-0 mb-4 bg-white p-4">
          {/* Header */}
          <div className="text-center mb-4 pb-3 border-bottom">
            <div className="badge bg-indigo-lt text-indigo px-3 py-1 mb-2 text-uppercase fw-bold letter-spacing-1">
              Entire Business Daily Register
            </div>
            <h1 className="fw-bold text-dark m-0">REGISTER — {formatDisplayDate(businessDate).toUpperCase()}</h1>
            <div className="text-muted mt-1">
              Status: <span className={`badge ${register.status === 'open' ? 'bg-success' : 'bg-secondary'} text-white`}>
                {(register.status || 'open').toUpperCase()}
              </span>
            </div>
          </div>

          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <div className="mt-2 text-muted">Aggregating shifts and daily totals...</div>
            </div>
          )}

          {!isLoading && (
            <>
              {/* SECTION 13: SHIFTS IN THIS REGISTER */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="fw-bold text-uppercase text-secondary m-0">Shifts in this Business Date</h4>
                  <span className="badge bg-blue-lt">{shifts.length} Shifts Logged</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter table-hover table-bordered bg-white shadow-xs">
                    <thead className="table-light">
                      <tr>
                        <th>Shift #</th>
                        <th>Cashier</th>
                        <th>Open</th>
                        <th>Close</th>
                        <th className="text-end">Opening Float</th>
                        <th className="text-end">Closing Cash</th>
                        <th className="text-center">Status</th>
                        <th className="text-center" style={{ width: '80px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map(s => {
                        const openFloat = Number(s.opening_balance !== undefined && s.opening_balance !== null ? s.opening_balance : (s.opening_cash || 0));
                        const isClosed = Boolean(s.closed_at) || s.status === 'closed';
                        const closeCash = isClosed
                          ? Number(s.actual_cash_in_hand !== undefined && s.actual_cash_in_hand !== null ? s.actual_cash_in_hand : (s.closing_balance !== undefined && s.closing_balance !== null ? s.closing_balance : (s.closing_cash || 0)))
                          : null;
                        const diff = Number(s.cash_difference || 0);
                        const isBal = Math.abs(diff) < 0.01;
                        const isActive = !isClosed || s.status === 'open' || s.status === 'active';

                        return (
                          <tr key={s.id}>
                            <td className="fw-bold">Shift #{s.shift_number || s.id}</td>
                            <td>{s.cashier_name || 'Staff'}</td>
                            <td className="small text-muted">{new Date(s.opened_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="small text-muted">{s.closed_at ? new Date(s.closed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <span className="badge bg-blue-lt">Active</span>}</td>
                            <td className="text-end fw-semibold">{currency} {openFloat.toFixed(2)}</td>
                            <td className="text-end fw-semibold">
                              {isClosed ? (
                                `${currency} ${(closeCash || 0).toFixed(2)}`
                              ) : (
                                <span className="badge bg-blue-lt">Active Session</span>
                              )}
                            </td>
                            <td className="text-center">
                              <span className={`badge ${isActive ? 'bg-primary' : isBal ? 'bg-success' : 'bg-warning'} text-white`}>
                                {isActive ? 'ACTIVE' : isBal ? 'Balanced' : `Variance (${diff > 0 ? '+' : ''}${diff.toFixed(2)})`}
                              </span>
                            </td>
                            <td className="text-center">
                              <button 
                                className="btn btn-sm btn-icon btn-ghost-primary"
                                aria-label="View Shift Full Report"
                                onClick={() => setSelectedShiftForReport(s)}
                              >
                                <IconEye size={17} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {shifts.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-3">No cashier shifts recorded for this business date.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SECTION 14 & 15: REGISTER SALES & REGISTER COLLECTIONS */}
              <div className="row g-4 mb-4">
                {/* REGISTER SALES */}
                <div className="col-lg-6">
                  <div className="border rounded-3 p-3 bg-light-lt h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                      <h4 className="fw-bold text-uppercase text-secondary m-0">Register Sales (All Shifts)</h4>
                      <span className="badge bg-purple-lt">Combined Revenue</span>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-vcenter table-bordered bg-white shadow-xs">
                        <thead className="table-light">
                          <tr>
                            <th>Item</th>
                            <th className="text-end">Amount</th>
                            <th className="text-center" style={{ width: '80px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="fw-medium">Gross Sales</td>
                            <td className="text-end fw-bold">{currency} {grossSales.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('Register Gross Sales')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-medium text-danger">Discount</td>
                            <td className="text-end text-danger fw-bold">-{currency} {discount.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('Discounted Invoices', i => Number(i.discount || 0) > 0)}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Net Sales</td>
                            <td className="text-end fw-bold">{currency} {netSales.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('Net Sales Invoices')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-medium">VAT / Tax</td>
                            <td className="text-end fw-bold text-indigo">{currency} {tax.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('VAT Invoices', i => Number(i.tax_total || 0) > 0)}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-medium">Round Off</td>
                            <td className="text-end text-muted">{roundOff < 0 ? '-' : ''}{currency} {Math.abs(roundOff).toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('Round Off Logs')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-medium text-purple">Complementary</td>
                            <td className="text-end fw-bold text-purple">{currency} {complementary.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-primary" onClick={() => openDrillDown('Complementary Items')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr className="table-primary border-top border-dark">
                            <td className="fw-bold fs-3 text-primary">Total Billed</td>
                            <td className="text-end fw-bold fs-3 text-primary">{currency} {totalBilled.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-primary" onClick={() => openDrillDown('All Billed Invoices')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                          <tr className="bg-yellow-lt">
                            <td className="fw-bold text-warning">Sales Due</td>
                            <td className="text-end fw-bold text-warning">{currency} {salesDue.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-ghost-warning" onClick={() => openDrillDown('Due Invoices', i => Number(i.due_amount || 0) > 0)}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* REGISTER COLLECTIONS */}
                <div className="col-lg-6">
                  <div className="border rounded-3 p-3 bg-light-lt h-100">
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                      <h4 className="fw-bold text-uppercase text-secondary m-0">Register Collections (All Shifts)</h4>
                      <span className="badge bg-green-lt">Payment Breakdown</span>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-vcenter table-bordered bg-white shadow-xs">
                        <thead className="table-light">
                          <tr>
                            <th>Payment Type</th>
                            <th className="text-end">Amount</th>
                            <th className="text-center" style={{ width: '80px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((p, idx) => (
                            <tr key={idx}>
                              <td className="fw-medium">{p.payment_method}</td>
                              <td className="text-end fw-bold">{currency} {Number(p.total || 0).toFixed(2)}</td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary"
                                  aria-label={`View all ${p.payment_method} transactions on this date`}
                                  onClick={() => openDrillDown(`All ${p.payment_method} Transactions (${businessDate})`, i => (i.payment_method_name || 'Cash').toLowerCase().includes(p.payment_method.toLowerCase()))}
                                >
                                  <IconEye size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {payments.length === 0 && (
                            <tr>
                              <td colSpan={3} className="text-center text-muted py-3">No payments received on this business date.</td>
                            </tr>
                          )}
                          <tr className="table-success border-top border-dark">
                            <td className="fw-bold fs-3 text-success">Total Collected</td>
                            <td className="text-end fw-bold fs-3 text-success">{currency} {totalCollected.toFixed(2)}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-icon btn-success" onClick={() => openDrillDown('All Collected Transactions')}>
                                <IconEye size={16} />
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Cash Drawer Flow in Register */}
                    <div className="card mt-3 border-0 bg-white shadow-xs p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold text-dark">Physical Cash Movement:</span>
                        <span className="fw-bold text-success">+{currency} {cashPayments.toFixed(2)} (Cash Sales)</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1 text-danger small">
                        <span>Physical Cash Outflow:</span>
                        <span>-{currency} {cashExpenses.toFixed(2)} (Cash Expenses)</span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between align-items-center fw-bold">
                        <span>Net Daily Cash Inflow:</span>
                        <span className={netCashMovement >= 0 ? 'text-primary' : 'text-danger'}>
                          {currency} {netCashMovement.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions & Print */}
              <div className="d-flex justify-content-end gap-2 d-print-none flex-wrap">
                <button 
                  className="btn btn-outline-warning text-dark font-medium"
                  onClick={() => openDrillDown('Move / Append Invoices to Sales Date')}
                  aria-label="Move midnight or late-night settlements to yesterday's or another business date"
                >
                  <IconArrowsExchange size={16} className="me-1 text-warning" /> Append Invoices to Sales Date ({transactions.length})
                </button>
                <button 
                  className="btn btn-outline-secondary" 
                  onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'register', shift: null })}
                >
                  <IconPrinter size={16} className="me-1" /> Print Register Summary
                </button>
                <button className="btn btn-primary" onClick={() => openDrillDown('Detailed Register Transactions')}>
                  View All Daily Transactions ({transactions.length})
                </button>
              </div>
            </>
          )}
        </div>

        {/* DRILL-DOWN AUDIT MODAL */}
        {drillModal.isOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-5xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      {drillModal.title}
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Business date traceability across all cashier shifts
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  {selectedInvoiceIds.length > 0 && (
                    <button
                      type="button"
                      className="px-3 py-1 rounded-xl text-xs font-bold bg-amber-400 text-zinc-900 hover:bg-amber-300 flex items-center gap-1.5 shadow-sm transition active:scale-95"
                      onClick={() => {
                        const selectedItems = filteredDrillItems.filter(it => selectedInvoiceIds.includes(it.id));
                        openReassignModal(selectedItems);
                      }}
                    >
                      <IconArrowsExchange size={15} />
                      Append {selectedInvoiceIds.length} to Sales Date
                    </button>
                  )}
                  <span className="px-3 py-1 rounded-xl text-xs font-bold bg-white/15 text-white border border-white/20">
                    {filteredDrillItems.length} Records
                  </span>
                  <button 
                    type="button" 
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                    onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
                  >
                    <IconX size={20} />
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="p-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/50 shrink-0">
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">
                    <IconSearch size={18} />
                  </span>
                  <input 
                    type="text" 
                    className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A] transition" 
                    placeholder="Search invoice number, customer or payment method..." 
                    value={drillSearch}
                    onChange={(e) => setDrillSearch(e.target.value)}
                    autoFocus
                  />
                  {drillSearch && (
                    <button 
                      type="button" 
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition"
                      onClick={() => setDrillSearch('')}
                    >
                      <IconX size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-y-auto flex-1 p-0">
                <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
                  <thead className="sticky top-0 bg-zinc-100 dark:bg-zinc-800/90 backdrop-blur-xs text-zinc-600 dark:text-zinc-300 uppercase tracking-wider font-bold border-b border-zinc-200 dark:border-zinc-700 z-10">
                    <tr>
                      <th className="py-3 px-3 text-center" style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          checked={filteredDrillItems.length > 0 && selectedInvoiceIds.length === filteredDrillItems.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedInvoiceIds(filteredDrillItems.map(it => it.id));
                            } else {
                              setSelectedInvoiceIds([]);
                            }
                          }}
                        />
                      </th>
                      <th className="py-3 px-3.5 text-center" style={{ width: '40px' }}>#</th>
                      <th className="py-3 px-3">Invoice #</th>
                      <th className="py-3 px-3">Time</th>
                      <th className="py-3 px-3">Customer</th>
                      <th className="py-3 px-3">Payment Type</th>
                      <th className="py-3 px-3 text-end">Amount ({currency})</th>
                      <th className="py-3 px-3 text-center" style={{ width: '130px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {filteredDrillItems.map((item, idx) => (
                      <tr key={idx} className="hover:bg-emerald-50/40 dark:hover:bg-zinc-800/50 transition-colors">
                        <td className="py-3 px-3 text-center">
                          <input
                            type="checkbox"
                            className="checkbox checkbox-xs"
                            checked={selectedInvoiceIds.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedInvoiceIds(prev => [...prev, item.id]);
                              } else {
                                setSelectedInvoiceIds(prev => prev.filter(id => id !== item.id));
                              }
                            }}
                          />
                        </td>
                        <td className="py-3 px-3.5 text-center text-zinc-400">{idx + 1}</td>
                        <td className="py-3 px-3 font-bold text-zinc-900 dark:text-white">{item.invoice_no || `INV-${item.id}`}</td>
                        <td className="py-3 px-3 text-zinc-500 text-[11px]">{item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                        <td className="py-3 px-3 font-medium text-zinc-800 dark:text-zinc-200">{item.customer_name || 'Walk-in'}</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                            {item.payment_method_name || 'Cash'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-end font-bold text-zinc-900 dark:text-white">{currency} {Number(item.total || 0).toFixed(2)}</td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button 
                              type="button"
                              className="p-1.5 rounded-xl text-[#243922] dark:text-emerald-300 bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] dark:hover:bg-emerald-900 border border-[#DCE7DB] dark:border-emerald-800/40 transition active:scale-95 shadow-xs" 
                              aria-label="View Transaction Details "
                              onClick={() => handleViewInvoice(item)}
                            >
                              <IconEye size={16} />
                            </button>
                            <button 
                              type="button"
                              className="p-1.5 rounded-xl text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 hover:bg-amber-100 dark:hover:bg-amber-900 border border-amber-200 dark:border-amber-800/40 transition active:scale-95 shadow-xs" 
                              aria-label="Reassign / Append to Different Date Sales (e.g. Midnight Bill)"
                              onClick={() => openReassignModal([item])}
                            >
                              <IconArrowsExchange size={16} />
                            </button>
                            <button 
                              type="button"
                              className="p-1.5 rounded-xl text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition active:scale-95 shadow-xs" 
                              aria-label="Print Thermal Receipt"
                              onClick={() => handlePrintTransaction(item)}
                            >
                              <IconPrinter size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredDrillItems.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center text-zinc-400 py-12 text-sm">No transactions found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between shrink-0">
                <div className="text-xs font-bold text-zinc-500">
                  Records: {filteredDrillItems.length}
                  {selectedInvoiceIds.length > 0 && (
                    <span className="ms-2 text-amber-600 dark:text-amber-400 font-bold">({selectedInvoiceIds.length} selected)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedInvoiceIds.length > 0 && (
                    <button 
                      type="button" 
                      className="px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white transition active:scale-95 shadow-xs flex items-center gap-1"
                      onClick={() => {
                        const selectedItems = filteredDrillItems.filter(it => selectedInvoiceIds.includes(it.id));
                        openReassignModal(selectedItems);
                      }}
                    >
                      <IconArrowsExchange size={15} />
                      Append Selected ({selectedInvoiceIds.length}) to Sales Date
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95" 
                    onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* INVOICE DETAILS MODAL (when clicking 👁 on any transaction) */}
        {viewingInvoice && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setViewingInvoice(null)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-3xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Invoice #{viewingInvoice.invoiceNo || viewingInvoice.invoice_no || viewingInvoice.id}
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      {viewingInvoice.created_at || viewingInvoice.date 
                        ? new Date(viewingInvoice.created_at || viewingInvoice.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) 
                        : '-'}
                    </p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setViewingInvoice(null)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* Summary row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Customer</div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white truncate">{viewingInvoice.customer_name || 'Walk-in Customer'}</div>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Payment Method</div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white">{viewingInvoice.payment_method_name || 'Cash'}</div>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Payment Status</div>
                    <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                      viewingInvoice.payment_status === 'paid' 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                    }`}>
                      {viewingInvoice.payment_status || (Number(viewingInvoice.due_amount || 0) > 0 ? 'due' : 'paid')}
                    </span>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Token / Ref</div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white">{viewingInvoice.token_no ? `#${viewingInvoice.token_no}` : '-'}</div>
                  </div>
                </div>

                {/* Items breakdown */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Itemized Breakdown</h4>
                  <div className="border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-bold border-b border-zinc-200 dark:border-zinc-700">
                        <tr>
                          <th className="py-2.5 px-3 text-center" style={{ width: '40px' }}>#</th>
                          <th className="py-2.5 px-3">Item</th>
                          <th className="py-2.5 px-3 text-center">Qty</th>
                          <th className="py-2.5 px-3 text-end">Price ({currency})</th>
                          <th className="py-2.5 px-3 text-end">Total ({currency})</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                        {viewingInvoice.orders && viewingInvoice.orders.length > 0 ? (
                          viewingInvoice.orders.flatMap(o => o.items || []).map((it, idx) => (
                            <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                              <td className="py-2.5 px-3 text-center text-zinc-400">{idx + 1}</td>
                              <td className="py-2.5 px-3">
                                <div className="font-bold text-zinc-900 dark:text-white">{it.item_title || it.title}</div>
                                {it.variant_title && <div className="text-[11px] text-zinc-500">Variant: {it.variant_title}</div>}
                                {it.addons && it.addons.length > 0 && (
                                  <div className="text-[11px] text-zinc-500">
                                    Addons: {it.addons.map(a => a.title || a.name).join(', ')}
                                  </div>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-center font-bold">{it.quantity}</td>
                              <td className="py-2.5 px-3 text-end text-zinc-500">{Number(it.price || 0).toFixed(2)}</td>
                              <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number((it.price * it.quantity) || 0).toFixed(2)}</td>
                            </tr>
                          ))
                        ) : viewingInvoice.item_name ? (
                          <tr>
                            <td className="py-2.5 px-3 text-center">1</td>
                            <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">{viewingInvoice.item_name}</td>
                            <td className="py-2.5 px-3 text-center font-bold">{viewingInvoice.quantity || 1}</td>
                            <td className="py-2.5 px-3 text-end text-zinc-500">{Number(viewingInvoice.price || 0).toFixed(2)}</td>
                            <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number(viewingInvoice.total || 0).toFixed(2)}</td>
                          </tr>
                        ) : (
                          <tr>
                            <td className="py-2.5 px-3 text-center">1</td>
                            <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">Order Items (Summary)</td>
                            <td className="py-2.5 px-3 text-center font-bold">1</td>
                            <td className="py-2.5 px-3 text-end text-zinc-500">{Number(viewingInvoice.subtotal || viewingInvoice.sub_total || viewingInvoice.total || 0).toFixed(2)}</td>
                            <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white">{Number(viewingInvoice.subtotal || viewingInvoice.sub_total || viewingInvoice.total || 0).toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Breakdown card */}
                <div className="flex justify-end">
                  <div className="w-full sm:w-80 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Subtotal:</span>
                      <span className="font-bold text-zinc-900 dark:text-white">{currency} {Number(viewingInvoice.subtotal !== undefined ? viewingInvoice.subtotal : (viewingInvoice.sub_total || 0)).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.discount || 0) > 0 && (
                      <div className="flex justify-between text-rose-600 font-medium">
                        <span>Discount:</span>
                        <span>-{currency} {Number(viewingInvoice.discount).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">VAT / Tax:</span>
                        <span className="font-bold text-zinc-900 dark:text-white">+{currency} {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(viewingInvoice.roundOff || viewingInvoice.round_off || 0) !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Round Off:</span>
                        <span className="font-bold text-zinc-900 dark:text-white">{currency} {Number(viewingInvoice.roundOff || viewingInvoice.round_off).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex justify-between text-sm font-black">
                      <span className="text-zinc-900 dark:text-white">Total Amount:</span>
                      <span className="text-[#243922] dark:text-emerald-400">{currency} {Number(viewingInvoice.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <span>Paid Amount:</span>
                      <span>{currency} {Number(viewingInvoice.paidAmount !== undefined ? viewingInvoice.paidAmount : (viewingInvoice.paid_amount !== undefined ? viewingInvoice.paid_amount : viewingInvoice.total)).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : (viewingInvoice.due_amount || 0)) > 0 && (
                      <div className="flex justify-between text-amber-600 font-bold">
                        <span>Due Amount:</span>
                        <span>{currency} {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : viewingInvoice.due_amount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between shrink-0 flex-wrap gap-2">
                <button 
                  type="button" 
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95" 
                  onClick={() => setViewingInvoice(null)}
                >
                  Close
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-3.5 py-2.5 rounded-xl text-xs font-bold text-amber-800 dark:text-amber-200 bg-amber-100 hover:bg-amber-200 dark:bg-amber-950/70 dark:hover:bg-amber-900 border border-amber-300/60 dark:border-amber-800/40 shadow-xs transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => {
                      const inv = viewingInvoice;
                      setViewingInvoice(null);
                      openReassignModal([inv]);
                    }}
                    aria-label="Append / Move this bill to yesterday's or another sales date"
                  >
                    <IconArrowsExchange size={16} />
                    Append to Sales Date
                  </button>
                  <button 
                    type="button" 
                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => handlePrintTransaction(viewingInvoice)}
                  >
                    <IconPrinter size={16} />
                    Print Thermal Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SHIFT FULL REPORT AUDIT MODAL */}
        {selectedShiftForReport && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setSelectedShiftForReport(null)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-4xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-5 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/50 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-black">
                    <IconFileText size={22} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-black text-zinc-900 dark:text-white m-0">
                        Shift #{selectedShiftForReport.shift_number || selectedShiftForReport.id} Full Audit Report
                      </h3>
                      <span className={`badge ${(!selectedShiftForReport.closed_at || selectedShiftForReport.status === 'open') ? 'bg-primary' : Math.abs(Number(selectedShiftForReport.cash_difference || 0)) < 0.01 ? 'bg-success' : 'bg-warning'} text-white text-2xs`}>
                        {(!selectedShiftForReport.closed_at || selectedShiftForReport.status === 'open') ? 'ACTIVE / OPEN' : Math.abs(Number(selectedShiftForReport.cash_difference || 0)) < 0.01 ? 'BALANCED' : 'VARIANCE'}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><strong>Cashier:</strong> {selectedShiftForReport.cashier_name || 'Staff'}</span>
                      <span><strong>Opened:</strong> {formatDateTime(selectedShiftForReport.opened_at)}</span>
                      <span><strong>Closed:</strong> {selectedShiftForReport.closed_at ? formatDateTime(selectedShiftForReport.closed_at) : 'Still Active'}</span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-8 h-8 rounded-full bg-zinc-200/60 dark:bg-zinc-800 flex items-center justify-center text-zinc-500 hover:text-zinc-800 dark:hover:text-white transition"
                  onClick={() => setSelectedShiftForReport(null)}
                >
                  <IconX size={18} />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="p-5 overflow-y-auto space-y-5 text-zinc-800 dark:text-zinc-200">
                {/* 4 KPI Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/60">
                    <div className="text-2xs font-bold uppercase tracking-wider text-zinc-500">Opening Float</div>
                    <div className="text-lg font-black text-zinc-900 dark:text-white mt-1">
                      {currency} {fmt(selectedShiftForReport.opening_balance !== undefined ? selectedShiftForReport.opening_balance : (selectedShiftForReport.opening_cash || 0))}
                    </div>
                    <div className="text-3xs text-zinc-400 mt-0.5">Initial Drawer Cash</div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40">
                    <div className="text-2xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400">Expected Drawer</div>
                    <div className="text-lg font-black text-indigo-900 dark:text-indigo-300 mt-1">
                      {currency} {fmt(selectedShiftForReport.system_expected_cash !== undefined ? selectedShiftForReport.system_expected_cash : (selectedShiftForReport.expected_cash || 0))}
                    </div>
                    <div className="text-3xs text-indigo-500 mt-0.5">System Calculation</div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40">
                    <div className="text-2xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Actual Cash Counted</div>
                    <div className="text-lg font-black text-emerald-900 dark:text-emerald-300 mt-1">
                      {selectedShiftForReport.closed_at ? (
                        `${currency} ${fmt(selectedShiftForReport.actual_cash_in_hand !== undefined && selectedShiftForReport.actual_cash_in_hand !== null ? selectedShiftForReport.actual_cash_in_hand : (selectedShiftForReport.closing_balance || 0))}`
                      ) : (
                        <span className="text-sm font-semibold text-zinc-500">Active (Pending Close)</span>
                      )}
                    </div>
                    <div className="text-3xs text-emerald-600 mt-0.5">Physical Cash Handover</div>
                  </div>

                  {(() => {
                    const diff = Number(selectedShiftForReport.cash_difference || 0);
                    const isClosed = Boolean(selectedShiftForReport.closed_at);
                    const isBal = Math.abs(diff) < 0.01;
                    return (
                      <div className={`p-3.5 rounded-2xl border ${!isClosed ? 'bg-zinc-50 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700/60' : isBal ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900' : diff > 0 ? 'bg-blue-50/70 border-blue-200 text-blue-900' : 'bg-rose-50/70 border-rose-200 text-rose-900'}`}>
                        <div className="text-2xs font-bold uppercase tracking-wider text-zinc-500">Cash Variance</div>
                        <div className="text-lg font-black mt-1">
                          {!isClosed ? (
                            <span className="text-sm font-semibold text-zinc-500">In Session</span>
                          ) : isBal ? (
                            <span className="text-emerald-700">Balanced (0.00)</span>
                          ) : diff > 0 ? (
                            <span className="text-blue-700">+{currency} {fmt(diff)} (Surplus)</span>
                          ) : (
                            <span className="text-rose-700">{currency} {fmt(diff)} (Short)</span>
                          )}
                        </div>
                        <div className="text-3xs text-zinc-400 mt-0.5">Physical vs Expected</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Section: Sales Summary & Collections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sales Breakdown */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/80 shadow-xs">
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-zinc-100 dark:border-zinc-700/60">
                      <h4 className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300 m-0">1. Sales Revenue Summary</h4>
                      <span className="text-2xs text-zinc-400">{selectedShiftForReport.total_invoices || selectedShiftForReport.stats?.total_invoices || 0} Bills</span>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                        <span className="text-zinc-500">Gross Sales (Items Total):</span>
                        <span className="font-mono font-bold text-zinc-800 dark:text-zinc-200">{currency} {fmt(selectedShiftForReport.gross_sales || selectedShiftForReport.stats?.gross_sales || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800 text-rose-600">
                        <span>Total Discounts:</span>
                        <span className="font-mono font-bold">-{currency} {fmt(selectedShiftForReport.total_discount || selectedShiftForReport.stats?.total_discount || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800 font-semibold bg-zinc-50/70 dark:bg-zinc-800/40 px-1 rounded">
                        <span className="text-zinc-700 dark:text-zinc-300">Net Sales:</span>
                        <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.net_sales || selectedShiftForReport.stats?.net_sales || 0)}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                        <span className="text-zinc-500">VAT / Taxes Total:</span>
                        <span className="font-mono font-bold text-zinc-800 dark:text-zinc-200">+{currency} {fmt(selectedShiftForReport.tax_total || selectedShiftForReport.stats?.tax_total || 0)}</span>
                      </div>
                      {Number(selectedShiftForReport.round_off || selectedShiftForReport.stats?.round_off || 0) !== 0 && (
                        <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                          <span className="text-zinc-500">Round Off:</span>
                          <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.round_off || selectedShiftForReport.stats?.round_off || 0)}</span>
                        </div>
                      )}
                      <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex justify-between font-black text-sm">
                        <span className="text-zinc-900 dark:text-white">TOTAL BILLED SALES:</span>
                        <span className="text-[#243922] dark:text-emerald-400 font-mono">
                          {currency} {fmt(selectedShiftForReport.system_total_sales || selectedShiftForReport.stats?.bill_total || selectedShiftForReport.stats?.system_total_sales || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Collections Breakdown */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/80 shadow-xs">
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-zinc-100 dark:border-zinc-700/60">
                      <h4 className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300 m-0">2. Collections by Payment Mode</h4>
                      <span className="text-2xs text-zinc-400">Payment Breakdown</span>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      {Array.isArray(selectedShiftForReport.payment_breakdown) && selectedShiftForReport.payment_breakdown.length > 0 ? (
                        selectedShiftForReport.payment_breakdown.map((pm, idx) => (
                          <div key={idx} className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                            <span className="text-zinc-600 dark:text-zinc-300 font-medium">{pm.payment_method || 'Payment'}:</span>
                            <span className="font-mono font-bold">{currency} {fmt(pm.total_amount)}</span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                            <span className="text-zinc-600 dark:text-zinc-300 font-medium">Cash Sales:</span>
                            <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.system_cash_sales || selectedShiftForReport.stats?.system_cash_sales || 0)}</span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                            <span className="text-zinc-600 dark:text-zinc-300 font-medium">QR / Online:</span>
                            <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.system_qr_sales || selectedShiftForReport.stats?.system_qr_sales || 0)}</span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                            <span className="text-zinc-600 dark:text-zinc-300 font-medium">Card / POS:</span>
                            <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.system_card_sales || selectedShiftForReport.stats?.system_card_sales || 0)}</span>
                          </div>
                          {Number(selectedShiftForReport.system_other_sales || 0) > 0 && (
                            <div className="flex justify-between py-1 border-b border-zinc-50 dark:border-zinc-800">
                              <span className="text-zinc-600 dark:text-zinc-300 font-medium">Other Modes:</span>
                              <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.system_other_sales || 0)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {Number(selectedShiftForReport.dues_created || selectedShiftForReport.stats?.dues_created || 0) > 0 && (
                        <div className="flex justify-between py-1.5 px-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-400 font-semibold">
                          <span>New Customer Dues Created:</span>
                          <span className="font-mono font-bold">{currency} {fmt(selectedShiftForReport.dues_created || selectedShiftForReport.stats?.dues_created || 0)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section: Drawer Reconciliation & Dues/Expenses */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700/80">
                  <div className="flex items-center justify-between pb-2 mb-3 border-b border-zinc-200 dark:border-zinc-700">
                    <h4 className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300 m-0">3. Cash Drawer Reconciliation Statement</h4>
                    <span className="text-2xs text-zinc-500">Mathematical Audit</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    <div className="space-y-1.5">
                      <div className="flex justify-between py-0.5">
                        <span className="text-zinc-500">(+) Starting Opening Float:</span>
                        <span className="font-mono font-semibold">{currency} {fmt(selectedShiftForReport.opening_balance !== undefined ? selectedShiftForReport.opening_balance : (selectedShiftForReport.opening_cash || 0))}</span>
                      </div>
                      <div className="flex justify-between py-0.5 text-emerald-700 dark:text-emerald-400">
                        <span>(+) Cash Sales Collected:</span>
                        <span className="font-mono font-semibold">+{currency} {fmt(selectedShiftForReport.system_cash_sales || selectedShiftForReport.stats?.system_cash_sales || 0)}</span>
                      </div>
                      {Number(selectedShiftForReport.system_due_collected || selectedShiftForReport.stats?.system_due_collected || 0) > 0 && (
                        <div className="flex justify-between py-0.5 text-emerald-700 dark:text-emerald-400">
                          <span>(+) Past Dues Collected:</span>
                          <span className="font-mono font-semibold">+{currency} {fmt(selectedShiftForReport.system_due_collected || selectedShiftForReport.stats?.system_due_collected || 0)}</span>
                        </div>
                      )}
                      {Number(selectedShiftForReport.system_expenses || selectedShiftForReport.stats?.system_expenses || 0) > 0 && (
                        <div className="flex justify-between py-0.5 text-rose-600">
                          <span>(-) Drawer Expenses Paid:</span>
                          <span className="font-mono font-semibold">-{currency} {fmt(selectedShiftForReport.system_cash_expenses !== undefined ? selectedShiftForReport.system_cash_expenses : (selectedShiftForReport.system_expenses || 0))}</span>
                        </div>
                      )}
                      {Number(selectedShiftForReport.system_purchase_payments || selectedShiftForReport.stats?.system_purchase_payments || 0) > 0 && (
                        <div className="flex justify-between py-0.5 text-rose-600">
                          <span>(-) Drawer Purchases Paid:</span>
                          <span className="font-mono font-semibold">-{currency} {fmt(selectedShiftForReport.system_cash_purchases !== undefined ? selectedShiftForReport.system_cash_purchases : (selectedShiftForReport.system_purchase_payments || 0))}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5 border-t sm:border-t-0 sm:border-l border-zinc-200 dark:border-zinc-700 pt-2 sm:pt-0 sm:ps-4 flex flex-col justify-center">
                      <div className="flex justify-between py-1 font-bold text-indigo-900 dark:text-indigo-300 bg-indigo-50/60 dark:bg-indigo-950/30 px-2 rounded-lg">
                        <span>Expected Drawer Cash:</span>
                        <span className="font-mono">{currency} {fmt(selectedShiftForReport.system_expected_cash !== undefined ? selectedShiftForReport.system_expected_cash : (selectedShiftForReport.expected_cash || 0))}</span>
                      </div>
                      <div className="flex justify-between py-1 font-bold text-zinc-900 dark:text-white px-2">
                        <span>Actual Physical Cash Counted:</span>
                        <span className="font-mono">
                          {selectedShiftForReport.closed_at ? (
                            `${currency} ${fmt(selectedShiftForReport.actual_cash_in_hand !== undefined && selectedShiftForReport.actual_cash_in_hand !== null ? selectedShiftForReport.actual_cash_in_hand : (selectedShiftForReport.closing_balance || 0))}`
                          ) : (
                            <span className="text-zinc-400">Active (Not Closed)</span>
                          )}
                        </span>
                      </div>
                      {selectedShiftForReport.closed_at && (
                        <div className={`flex justify-between py-1 px-2 rounded-lg font-black ${Math.abs(Number(selectedShiftForReport.cash_difference || 0)) < 0.01 ? 'bg-emerald-100 text-emerald-900' : Number(selectedShiftForReport.cash_difference || 0) > 0 ? 'bg-blue-100 text-blue-900' : 'bg-rose-100 text-rose-900'}`}>
                          <span>Cash Variance:</span>
                          <span className="font-mono">
                            {Number(selectedShiftForReport.cash_difference || 0) > 0 ? '+' : ''}{currency} {fmt(selectedShiftForReport.cash_difference || 0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section: Denominations breakdown (Opening & Closing) */}
                {(() => {
                  const openDenoms = parseDenoms(selectedShiftForReport.start_denominations);
                  const closeDenoms = parseDenoms(selectedShiftForReport.denominations);
                  if (openDenoms.length === 0 && closeDenoms.length === 0) return null;

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Starting float denoms */}
                      <div className="p-3.5 rounded-2xl bg-white dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/80">
                        <div className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300 pb-1.5 mb-2 border-b">
                          Opening Float Denominations
                        </div>
                        {openDenoms.length > 0 ? (
                          <div className="space-y-2">
                            {openDenoms.map((grp, gIdx) => (
                              <table key={gIdx} className="w-full text-xs text-left">
                                <thead>
                                  <tr className="border-b text-2xs text-zinc-400">
                                    <th className="py-1">Denom</th>
                                    <th className="py-1 text-center">Count</th>
                                    <th className="py-1 text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(grp.breakdown || []).map((it, iIdx) => (
                                    <tr key={iIdx} className="border-b border-zinc-50 dark:border-zinc-800">
                                      <td className="py-1 font-mono">{grp.currency_code || currency} {fmt(it.denomination)}</td>
                                      <td className="py-1 text-center font-bold">{it.count}</td>
                                      <td className="py-1 text-right font-mono">{fmt(it.total || (it.denomination * it.count))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-zinc-400 italic py-2">No opening itemized count recorded.</div>
                        )}
                      </div>

                      {/* Closing denoms */}
                      <div className="p-3.5 rounded-2xl bg-white dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/80">
                        <div className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300 pb-1.5 mb-2 border-b">
                          Closing Drawer Denominations
                        </div>
                        {closeDenoms.length > 0 ? (
                          <div className="space-y-2">
                            {closeDenoms.map((grp, gIdx) => (
                              <table key={gIdx} className="w-full text-xs text-left">
                                <thead>
                                  <tr className="border-b text-2xs text-zinc-400">
                                    <th className="py-1">Denom</th>
                                    <th className="py-1 text-center">Count</th>
                                    <th className="py-1 text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(grp.breakdown || []).map((it, iIdx) => (
                                    <tr key={iIdx} className="border-b border-zinc-50 dark:border-zinc-800">
                                      <td className="py-1 font-mono">{grp.currency_code || currency} {fmt(it.denomination)}</td>
                                      <td className="py-1 text-center font-bold">{it.count}</td>
                                      <td className="py-1 text-right font-mono">{fmt(it.total || (it.denomination * it.count))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-zinc-400 italic py-2">No closing itemized count recorded.</div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Notes */}
                {selectedShiftForReport.notes && (
                  <div className="p-3 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40 text-xs">
                    <span className="font-bold text-amber-900 dark:text-amber-400">Shift Notes / Remarks: </span>
                    <span className="text-zinc-700 dark:text-zinc-300">{selectedShiftForReport.notes}</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95" 
                  onClick={() => setSelectedShiftForReport(null)}
                >
                  Close
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-3.5 py-2.5 rounded-xl text-xs font-semibold text-zinc-700 dark:text-zinc-200 bg-zinc-200/70 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_open', shift: selectedShiftForReport })}
                  >
                    <IconPrinter size={15} />
                    Print Float Slip
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_close', shift: selectedShiftForReport })}
                  >
                    <IconPrinter size={15} />
                    Print Shift Z-Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FINANCIAL REPORTS PRINT MODAL */}
        {printModalConfig.isOpen && (
          <FinancialReportsPrintModal 
            isOpen={printModalConfig.isOpen}
            onClose={() => setPrintModalConfig(prev => ({ ...prev, isOpen: false }))}
            reportType={printModalConfig.reportType || "register"}
            selectedShift={printModalConfig.shift || null}
            selectedRegister={data}
            dateRange={{ from: selectedDate, to: selectedDate }}
            data={{
              registerSummary: data,
              invoices: data?.invoices || {},
              payments: data?.payments || [],
              transactions: data?.transactions || [],
              shifts: data?.shifts || [],
              shift: printModalConfig.shift || null
            }}
          />
        )}

        {/* REASSIGN / APPEND TO SALES DATE MODAL */}
        {reassignModal.isOpen && (
          <div 
            className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
            onClick={() => setReassignModal(prev => ({ ...prev, isOpen: false }))}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-lg overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-150 p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b pb-3 border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 flex items-center justify-center border border-amber-200/50">
                    <IconArrowsExchange size={22} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-zinc-900 dark:text-white m-0">
                      Append Invoices to Target Sales Date
                    </h3>
                    <p className="text-xs text-zinc-500 m-0">
                      Reassign midnight or shift settlements to the correct business day
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReassignModal(prev => ({ ...prev, isOpen: false }))}
                  className="text-zinc-400 hover:text-zinc-600 transition"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Selected Invoices Summary */}
              <div className="bg-zinc-50 dark:bg-zinc-800/50 p-3.5 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 text-xs space-y-2">
                <div className="flex justify-between font-semibold text-zinc-700 dark:text-zinc-300">
                  <span>Invoices Selected:</span>
                  <span className="font-mono bg-zinc-200 dark:bg-zinc-700 px-2 py-0.5 rounded-md font-bold">
                    {reassignModal.invoices.length} bill(s)
                  </span>
                </div>
                <div className="flex justify-between text-zinc-600 dark:text-zinc-400">
                  <span>Bill Numbers:</span>
                  <span className="font-mono truncate max-w-[240px] text-right">
                    {reassignModal.invoices.map(i => i.invoice_no || `INV-${i.id}`).join(', ')}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-emerald-600 dark:text-emerald-400 pt-1 border-t border-zinc-200 dark:border-zinc-700">
                  <span>Total Sales Amount:</span>
                  <span className="font-mono text-sm">
                    {currency} {reassignModal.invoices.reduce((sum, it) => sum + Number(it.total || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Info Callout */}
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3.5 rounded-2xl border border-amber-200/60 dark:border-amber-900/30 text-xs text-amber-800 dark:text-amber-200/90 leading-relaxed">
                <strong>💡 Midnight Settlement Fix:</strong> When a bill is settled past midnight (e.g. 12:01 AM), the system timestamp defaults to the new day register. Moving it to the target sales date updates the invoice, payment, and order records so that the chosen date's register, shifts, day book, and sales reports reflect this transaction.
              </div>

              {/* Target Date & Time Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block font-bold text-zinc-700 dark:text-zinc-300 mb-1">
                    Target Sales Date *
                  </label>
                  <input
                    type="date"
                    className="input input-sm input-bordered w-full text-xs font-mono"
                    value={reassignModal.targetDate}
                    onChange={(e) => setReassignModal(prev => ({ ...prev, targetDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block font-bold text-zinc-700 dark:text-zinc-300 mb-1">
                    Target Time (HH:mm)
                  </label>
                  <input
                    type="time"
                    className="input input-sm input-bordered w-full text-xs font-mono"
                    value={reassignModal.targetTime}
                    onChange={(e) => setReassignModal(prev => ({ ...prev, targetTime: e.target.value }))}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <button
                  type="button"
                  disabled={isReassigning}
                  onClick={() => setReassignModal(prev => ({ ...prev, isOpen: false }))}
                  className="btn btn-sm btn-ghost text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isReassigning || !reassignModal.targetDate}
                  onClick={handleConfirmReassign}
                  className="btn btn-sm bg-[#70B56A] hover:bg-[#5fa359] text-white font-bold text-xs gap-1 border-none shadow-xs"
                >
                  {isReassigning ? (
                    <>
                      <IconRefresh className="animate-spin" size={15} />
                      Appending Sales...
                    </>
                  ) : (
                    <>
                      <IconCheck size={15} />
                      Confirm & Append to {reassignModal.targetDate}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Page>
  );
}
